/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnInit,
} from '@angular/core';
import { UIRouterModule } from '@uirouter/angular';
import { FeedStoreService } from '../services/feed-store.service';
import { SharedModule } from '../shared/shared.module';
import { itemCommentCount } from '../common/community/feed/community-item';
import { createFailureAwareStore } from '../shared/failure-aware-store';
import { focusNewItem } from '../shared/focus-new-item';
import { EmojiHtmlPipe } from '../pipes/emoji-html.pipe';
import { UtCategoryLinkModule } from '../common/community/feed/category-link/category-link.module';

// /my-forums in the 2026 design: topics you started or replied to, as topic rows. Same query as
// MyDiscussionsCtrl (commented_on: you, sorted by -commented_on_at); adds an empty state.
@Component({
  selector: 'ut-my-forums-page',
  standalone: true,
  imports: [UIRouterModule, SharedModule, EmojiHtmlPipe, UtCategoryLinkModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './my-forums-page.component.html',
  styleUrls: ['./my-forums-page.component.scss'],
})
export class MyForumsPageComponent implements OnInit {
  // The state's `user` resolve; the state requires login.
  @Input({ required: true }) user: any;

  readonly replyCount = itemCommentCount;

  store: any;

  constructor(
    @Inject('DiscussionService') private DiscussionService: any,
    private feedStore: FeedStoreService,
    private cdr: ChangeDetectorRef,
    private el: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.store = createFailureAwareStore(
      this.feedStore,
      (params) => this.DiscussionService.feed(params),
      { filter: { commented_on: this.user.id }, sort: '-commented_on_at' },
    );
    this.track(this.store.load());
  }

  get status(): string {
    if (this.store.isLoading) return 'Loading your topics';
    return `${this.store.feedItems.length} topics shown`;
  }

  retry(): void {
    this.track(this.store.feedItems.length ? this.store.loadMore() : this.store.load());
  }

  loadMore(): void {
    const next = this.store.feedItems.length;
    this.track(this.store.loadMore()).then(() => {
      const topic = this.store.feedItems[next];
      if (topic) focusNewItem(this.el.nativeElement, `#topic_${topic.id}`);
    });
  }

  // FeedStore mutates in place, so re-render when a page starts and when it lands.
  private track(promise: Promise<unknown>): Promise<unknown> {
    this.cdr.markForCheck();
    return promise.then(() => this.cdr.markForCheck());
  }
}
